import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getFormations } from '../services/formations';
import {
  runFusionAnalysis,
  listAnalyses,
  getSimilarityColor,
  getSimilarityLabel,
  formatTimeSaved,
  calculateFusionROI,
  sortSuggestionsByImpact,
  exportAnalysisToJSON,
  exportAnalysisToCSV
} from '../services/analysis';
import { captureEvent } from '../utils/posthog';

/**
 * Page d'analyse de fusion des formations
 */
export default function AnalysisPage() {
  const { user } = useAuth();

  const [formations, setFormations] = useState([]);
  const [selectedFormations, setSelectedFormations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [historyAnalyses, setHistoryAnalyses] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('new'); // 'new' ou 'history'

  useEffect(() => {
    loadUserFormations();
    loadAnalysisHistory();
  }, []);

  /**
   * Charger les formations de l'utilisateur
   */
  const loadUserFormations = async () => {
    if (!user?.organizationId) return;

    const result = await getFormations({
      organizationId: user.organizationId,
      page: 1,
      limit: 100,
      activeOnly: true
    });

    if (result.success) {
      setFormations(result.data);
    }
  };

  /**
   * Charger l'historique des analyses
   */
  const loadAnalysisHistory = async () => {
    const result = await listAnalyses({
      page: 1,
      limit: 10,
      organizationId: user?.organizationId
    });

    if (result.success) {
      setHistoryAnalyses(result.data);
    }
  };

  /**
   * Sélectionner/désélectionner une formation
   */
  const toggleFormationSelection = (formationId) => {
    if (selectedFormations.includes(formationId)) {
      setSelectedFormations(selectedFormations.filter(id => id !== formationId));
    } else {
      setSelectedFormations([...selectedFormations, formationId]);
    }
    setError('');
  };

  /**
   * Lancer l'analyse
   */
  const handleRunAnalysis = async () => {
    if (selectedFormations.length < 2) {
      setError('Veuillez sélectionner au moins 2 formations pour l\'analyse');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysisResult(null);

    const result = await runFusionAnalysis({
      organizationId: user.organizationId,
      formationIds: selectedFormations
    });

    setLoading(false);

    if (result.success) {
      setAnalysisResult(result.data);
      loadAnalysisHistory(); // Rafraîchir l'historique
      setActiveTab('result'); // Basculer vers les résultats
    } else {
      setError(result.error);
    }
  };

  /**
   * Afficher une analyse de l'historique
   */
  const handleViewHistory = (analysis) => {
    setAnalysisResult(analysis);
    setActiveTab('result');
  };

  const percentSimilarity = parseFloat(analysisResult?.percentSimilarity || 0);
  const timeSaved = parseFloat(analysisResult?.estimatedTimeSavedHours || 0);
  const roi = calculateFusionROI(timeSaved);
  const sortedSuggestions = sortSuggestionsByImpact(analysisResult?.suggestedMerges || []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Analyse de fusion des formations
          </h1>
          <p className="mt-2 text-gray-600">
            Identifiez les compétences partagées et optimisez votre catalogue
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('new')}
              className={`${
                activeTab === 'new'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Nouvelle analyse
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Historique ({historyAnalyses.length})
            </button>
            {analysisResult && (
              <button
                onClick={() => setActiveTab('result')}
                className={`${
                  activeTab === 'result'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Résultats
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content: New Analysis */}
        {activeTab === 'new' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Formation Selection */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Sélectionnez les formations à analyser
                </h2>

                {formations.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z" clipRule="evenodd" />
                    </svg>
                    <p>Aucune formation disponible</p>
                    <p className="text-sm mt-1">Chargez d'abord des formations depuis l'onboarding</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {formations.map((formation) => (
                      <label
                        key={formation.id}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                          selectedFormations.includes(formation.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedFormations.includes(formation.id)}
                          onChange={() => toggleFormationSelection(formation.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-semibold text-gray-900">{formation.title}</div>
                          <div className="text-sm text-gray-600">
                            {formation.code}
                            {formation.level && ` • Niveau ${formation.level}`}
                            {formation.competences?.length && ` • ${formation.competences.length} compétences`}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Action Panel */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Analyse
                </h3>

                <div className="mb-6">
                  <div className="text-sm text-gray-600 mb-2">Formations sélectionnées</div>
                  <div className="text-3xl font-bold text-blue-600">{selectedFormations.length}</div>
                  {selectedFormations.length < 2 && (
                    <p className="text-sm text-gray-500 mt-1">Minimum : 2 formations</p>
                  )}
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleRunAnalysis}
                  disabled={loading || selectedFormations.length < 2}
                  className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyse en cours...
                    </span>
                  ) : (
                    'Lancer l\'analyse'
                  )}
                </button>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    L'analyse vous permettra de :
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Identifier les compétences partagées
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Calculer le % de similarité
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Estimer le temps économisé
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Obtenir des suggestions de fusion
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content: History */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow">
            {historyAnalyses.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                <p>Aucune analyse effectuée</p>
                <p className="text-sm mt-1">Lancez votre première analyse pour voir l'historique</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {historyAnalyses.map((analysis) => {
                  const percent = parseFloat(analysis.percentSimilarity || 0);
                  const colorClass = getSimilarityColor(percent);

                  return (
                    <div key={analysis.id} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
                              {percent}% similarité
                            </span>
                            <span className="text-sm text-gray-500">
                              {new Date(analysis.createdAt).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {analysis.totalUniqueCompetences} compétences totales •
                            {analysis.numSimilarCompetences} partagées •
                            Gain: {formatTimeSaved(parseFloat(analysis.estimatedTimeSavedHours || 0))}
                          </div>
                        </div>
                        <button
                          onClick={() => handleViewHistory(analysis)}
                          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Voir détails
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tab Content: Results */}
        {activeTab === 'result' && analysisResult && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-1">Compétences totales</div>
                <div className="text-3xl font-bold text-gray-900">
                  {analysisResult.totalUniqueCompetences}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-1">Compétences partagées</div>
                <div className="text-3xl font-bold text-blue-600">
                  {analysisResult.numSimilarCompetences}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-1">Similarité</div>
                <div className={`text-3xl font-bold inline-flex items-center px-4 py-2 rounded-lg ${getSimilarityColor(percentSimilarity)}`}>
                  {percentSimilarity}%
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {getSimilarityLabel(percentSimilarity)}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-1">Temps économisé</div>
                <div className="text-3xl font-bold text-green-600">
                  {formatTimeSaved(timeSaved)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  ≈ {roi.moneySaved}€ économisés
                </div>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Exporter les résultats</h3>
              <div className="flex gap-4">
                <button
                  onClick={() => exportAnalysisToJSON(analysisResult)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  JSON
                </button>
                <button
                  onClick={() => exportAnalysisToCSV(analysisResult)}
                  className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  CSV
                </button>
              </div>
            </div>

            {/* Suggestions */}
            {sortedSuggestions.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Suggestions de fusion ({sortedSuggestions.length})
                </h3>
                <div className="space-y-4">
                  {sortedSuggestions.map((suggestion, index) => {
                    const impactColors = {
                      high: 'bg-red-100 text-red-800',
                      medium: 'bg-yellow-100 text-yellow-800',
                      low: 'bg-green-100 text-green-800'
                    };

                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 mb-1">
                              {suggestion.reason}
                            </div>
                            <div className="text-sm text-gray-600">
                              {suggestion.sharedCompetences} compétences partagées ({suggestion.percentShared}%)
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${impactColors[suggestion.impact]}`}>
                            Impact {suggestion.impact === 'high' ? 'fort' : suggestion.impact === 'medium' ? 'moyen' : 'faible'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
